import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { Card, Tooltip, Input, HeadItemType, LoadingModal, Table } from 'components'
import { useFormContext, useWatch } from 'react-hook-form'
import { Color, SizeDescription } from 'store/ProductContext/productContextTypes'
import { useDispatch, useSelector } from 'store/store'
import { MaterialByIdResponse } from 'store/ProductContext'
import * as DraftOV from 'services/draft'
import * as S from './styles'

import ProductFooter from '../ProductFooter'
import ItemSize from '../ItemSize'
import ProductDetail from '../ProductDetail'
import Subtotal from './Subtotal'
import SizesTooltip from '../SizesTooltip'
import { ProductContextState } from 'container/ProductPage'
import { useNavigate, useParams } from 'react-router-dom'
import BodyPostProduct from 'container/ProductPage/utils/BodyPostProduct'
import {
  setConfirmedNavigation,
  setShowPrompt
} from 'store/StructuralContext/structuralSlice'

import { TypesValidations } from 'constants/enums'
import { decimalMask } from 'commons/utils/mask'
import { Skeleton } from '@mui/material'
import { ensureNonNegative } from 'commons/utils/number'

interface ProductItemsProps {
  materialSelected: MaterialByIdResponse
  convertAmount: number
  defaultSizes: Record<string, string | string[] | boolean | Record<string, number>>
}

const ITEMS_TO_SHOW = 10

function ProductItems({
  materialSelected,
  convertAmount,
  defaultSizes
}: ProductItemsProps) {
  const { filter, matrixStock, loadingStock } = useSelector((state) => state.products)
  const methods = useFormContext()
  const { getValues, setValue } = methods
  const dispatch = useDispatch()
  const { draftBody, records } = useSelector(
    (stateSalesOrder) => stateSalesOrder.salesOrder
  )
  const { showDetailedStock } = useSelector((state) => state.structure)
  const { orderNumber } = useParams()
  const navigate = useNavigate()

  const [loading, setLoading] = useState(false)
  const [loadedItems, setLoadedItems] = useState<Color[]>([])
  const [itemsToShow, setItemsToShow] = useState(0)

  const memorizedMaterials = useMemo(() => materialSelected.cores, [materialSelected])

  const isMaterialListNotFull = itemsToShow <= memorizedMaterials.length

  const addNewItems = useMemo(() => {
    return memorizedMaterials.slice(
      loadedItems.length,
      loadedItems.length + ITEMS_TO_SHOW
    )
  }, [loadedItems])

  useEffect(() => {
    if (isMaterialListNotFull) {
      setLoadedItems((prevLoadedItems) => {
        return [...prevLoadedItems, ...addNewItems]
      })
    }

    setLoading(false)
  }, [itemsToShow, memorizedMaterials])

  const handleScroll = () => {
    setLoading(true)
    setItemsToShow((prev) => prev + ITEMS_TO_SHOW)
  }

  const productColors = useMemo(
    () => memorizedMaterials.map((item) => item.ds_cor),
    [memorizedMaterials]
  )

  const hideSkuStock = useMemo(
    () => /m|c/i.test(materialSelected?.tp_estq_exce),
    [materialSelected]
  )

  const isColorStock = useMemo(
    () =>
      /c/i.test(materialSelected?.tp_estq_exce) &&
      materialSelected?.fl_pedi_antp !== TypesValidations.YES,
    [materialSelected]
  )

  const materialDiscount = useMemo(() => {
    const selectedItem = records.find(
      (record) => record.nr_item_pedi_rasc === materialSelected.nr_item_pedi_rasc
    )

    if (!selectedItem?.pc_dsct) return draftBody.pc_dsct

    return +selectedItem.pc_dsct
  }, [materialSelected])

  const watchedMixedBox: boolean = useWatch({
    control: methods.control,
    name: 'fl_caix_grad'
  })

  const navigateBack = () => navigate(-1)
  const handleSaveWithOrderNumber = async (state: ProductContextState) => {
    let result = false
    setLoading(true)

    DraftOV.postMaintainItem(
      BodyPostProduct({
        state,
        materialSelected,
        filter,
        draftBody,
        defaultSizes,
        materialDiscount
      })
    )
      .then((response) => {
        dispatch(setConfirmedNavigation(true))

        if (response && response.nr_pedi_rasc) {
          result = true
          navigateBack()
          dispatch(setShowPrompt(false))
        }
      })
      .catch(() => {
        result = false
      })
      .finally(() => setLoading(false))

    return result
  }
  const handleSave = async (state: ProductContextState) => {
    let result = false
    setLoading(true)

    if (orderNumber) {
      const request = await handleSaveWithOrderNumber(state)
      result = request
    }

    return result
  }

  const getFilter = useCallback(
    (color: Color, arr: string[]) => {
      if (arr.length < 1) {
        return true
      }
      // TODO: Refactor to use cd_cor instead of ds_cor
      return arr.includes(color.ds_cor.toLowerCase())
    },
    [filter]
  )

  const sizesSet = useMemo(() => {
    const sizes = memorizedMaterials.flatMap((cor) =>
      cor.tamanhos.flatMap((tamanho) => {
        return { size: tamanho.cd_tamh, description: tamanho.componentes }
      })
    )

    sizes.sort((a, b) => a.size.localeCompare(b.size))

    const memoSet = new Set<string>()
    sizes.forEach((size) => memoSet.add(JSON.stringify(size)))
    return memoSet
  }, [materialSelected])

  const isCustomBox = getValues('fl_caix_grad')

  const headData = (): HeadItemType[] => {
    const sizes = [...sizesSet].map((size) => {
      const object: { size: string; description: SizeDescription[] } = JSON.parse(size)

      return {
        label: (
          <S.LabelStyled key={`${object.size}`}>
            {object.size}{' '}
            {!!object.description.length && (
              <SizesTooltip description={object.description} />
            )}
          </S.LabelStyled>
        ),
        columnName: object.size
      }
    })

    const tableHead: HeadItemType[] = [
      {
        label: 'Cor',
        columnName: 'Cor'
      },
      ...sizes
    ]

    if (!isCustomBox)
      tableHead.splice(1, 0, {
        label: 'Digitado',
        columnName: 'Digitado'
      })

    if (isColorStock)
      tableHead.splice(1, 0, {
        label: 'Estoque',
        columnName: 'Estoque'
      })

    return tableHead
  }

  function noReload(event: FormEvent) {
    event.preventDefault()
  }

  useEffect(() => {
    setLoading(false)
  }, [loadedItems])

  const currentFormValues = Object.entries(getValues()).filter((item) =>
    loadedItems.some((cor) => cor?.ds_cor === item[0])
  )

  const getRecordsData = useMemo(() => {
    const currentDirtyColors = currentFormValues.reduce(
      (acc, item: [string, Record<string, number>]) => {
        const colorSum = Object.values(item[1]).reduce(
          (colorAcc, value) => colorAcc + value,
          0
        )
        if (colorSum) return [...acc, item[0]]
        return acc
      },
      [] as string[]
    )

    const materials = filter.length ? memorizedMaterials : loadedItems

    const recordsData = materials
      .filter((cor) => getFilter(cor, filter))
      .map((cor) => {
        const colorStock = matrixStock.cores
          ? matrixStock.cores.find((c) => c.codigo === cor.cd_cor)
          : null

        const inputs = [...sizesSet].map((size) => {
          const object: { size: string; description: SizeDescription[] } =
            JSON.parse(size)

          const foundSize = cor.tamanhos.find((t) => t.cd_tamh === object.size)

          if (foundSize?.cd_tamh) {
            const sizeStock =
              colorStock && colorStock.tamanhos
                ? colorStock.tamanhos.find((size) => size.codigo === foundSize.cd_tamh)
                : null

            const setItemSizeDisabled = (): boolean | undefined => {
              if (materialSelected.fl_digi_caix_vari !== TypesValidations.YES) {
                return false
              }

              const dirtyFieldsByColor = currentDirtyColors?.filter((dirtyColors) =>
                productColors.includes(dirtyColors)
              )

              setValue('dirtyFieldsByColor', dirtyFieldsByColor)
              setValue('disabled_fl_caix_grad', dirtyFieldsByColor.length > 1)

              if (!watchedMixedBox || !dirtyFieldsByColor.length) return false

              return (
                dirtyFieldsByColor.length >= 2 &&
                dirtyFieldsByColor[0].includes(cor.ds_cor)
              )
            }

            return (
              <S.ItemBox key={`${cor.ds_cor}-${foundSize?.cd_tamh}`}>
                <ItemSize
                  colorName={cor.ds_cor}
                  size={foundSize?.cd_tamh}
                  disabled={setItemSizeDisabled()}
                />
                {showDetailedStock && !hideSkuStock && (
                  <>
                    {loadingStock ? (
                      <Skeleton width={30} height={17} animation="wave" />
                    ) : (
                      <span>
                        {sizeStock
                          ? decimalMask(ensureNonNegative(sizeStock.volumeDisponivel))
                          : 0}
                      </span>
                    )}
                  </>
                )}
              </S.ItemBox>
            )
          }

          return (
            <S.ItemBox key={`${cor.ds_cor}-${object.size}-not-exist`}>
              <Input
                value=""
                disabled
                height={30}
                width={80}
                InputProps={{
                  placeholder: '',
                  inputProps: { maxLength: 6 }
                }}
              />
            </S.ItemBox>
          )
        })

        const data = {
          id: `${cor.ds_cor}-${cor.cd_cor}`,
          content: [
            <S.ColorBor key={`${cor.cd_cor}-${cor.ds_cor}-sub`}>
              <S.ColorTitleStyled>
                <Tooltip
                  arrow
                  title={
                    <S.ToolTipBox>
                      <span id="title">
                        {cor.cd_cor} - {cor.ds_cor}
                      </span>
                    </S.ToolTipBox>
                  }
                  placement="top"
                >
                  <S.InfoOutlineIconStyled />
                </Tooltip>
                <span id="title">{cor.cd_cor}</span>
              </S.ColorTitleStyled>
              {cor.fl_matr_estt === TypesValidations.YES && (
                <span id="strategic">Estratégico</span>
              )}
              <S.ColorDescription>{cor.ds_cor}</S.ColorDescription>
            </S.ColorBor>,
            ...inputs
          ]
        }

        if (!isCustomBox) data.content.splice(1, 0, <Subtotal colorName={cor.ds_cor} />)
        if (isColorStock) {
          data.content.splice(
            1,
            0,
            <>
              {loadingStock ? (
                <Skeleton width={50} height={22} animation="wave" />
              ) : (
                <S.ColorStock>
                  {decimalMask(
                    colorStock?.totalDisponivel
                      ? ensureNonNegative(colorStock?.totalDisponivel)
                      : ''
                  )}{' '}
                </S.ColorStock>
              )}
            </>
          )
        }

        return data
      })

    return recordsData
  }, [
    filter,
    sizesSet,
    getValues,
    showDetailedStock,
    watchedMixedBox,
    productColors,
    loadedItems,
    loadingStock,
    matrixStock
  ])

  return (
    <form onSubmit={noReload}>
      {loading && <LoadingModal />}
      <Card>
        <S.BoxColumn>
          <ProductDetail convertAmount={convertAmount} />
          <Table
            distributeWidth={false}
            rowHeight={showDetailedStock ? 70 : 50}
            dense
            headData={headData()}
            fixedFirstCol
            fixedHead
            inputAlign={watchedMixedBox ? 'left' : 'center'}
            bodyData={getRecordsData}
            onScroll={handleScroll}
          />
        </S.BoxColumn>
      </Card>
      <ProductFooter
        onSave={handleSave}
        defaultSizes={defaultSizes}
        isLoading={loading}
      />
    </form>
  )
}

export default ProductItems
